/**
 * Iter218 · /security-assessment — Public 16-question vehicle security
 * assessment for Smith SecureVehicle™.
 *
 * Multi-step form: contact → 16 answers → results. Persists to
 * `smith_vehicle_security_assessments` via POST /api/vehicle-security/
 * assessment/submit.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Car, ShieldCheck, Loader2, ArrowRight, ArrowLeft, CheckCircle2,
  AlertTriangle, ChevronRight, Sparkles, Home, DollarSign, Radio,
  UserCheck, Package,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const SEGMENTS = [
  { id: "apartment_community", label: "Apartment community" },
  { id: "hoa",                 label: "Gated community / HOA" },
  { id: "warehouse",           label: "Warehouse / logistics" },
  { id: "auto_dealership",     label: "Auto dealership" },
  { id: "corporate_campus",    label: "Corporate campus" },
  { id: "self_storage",        label: "Self-storage" },
  { id: "shopping_center",     label: "Shopping center" },
  { id: "medical_campus",      label: "Medical / hospital campus" },
  { id: "other",               label: "Other" },
];


function useSeo() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Free Vehicle Security Assessment — Smith SecureVehicle™";
    return () => { document.title = prev; };
  }, []);
}


// ── Reusable field ─────────────────────────────────────────────────
function Field({ label, required, children, hint }) {
  return (
    <div>
      <label className="text-[10.5px] uppercase tracking-widest text-slate-400 font-bold block mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      {children}
      {hint && <div className="text-[10.5px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}


// ── Step 1: contact info ───────────────────────────────────────────
function Step1({ contact, setContact, onNext }) {
  const canContinue =
    contact.name?.trim().length >= 2 &&
    /.+@.+\..+/.test(contact.email || "") &&
    contact.segment;

  return (
    <div className="space-y-5" data-testid="assess-step1">
      <div>
        <div className="text-[10.5px] uppercase tracking-widest text-red-300 font-bold mb-1">
          Step 1 of 3
        </div>
        <h2 className="text-lg md:text-2xl font-extrabold text-white">
          Tell us where to send your score
        </h2>
        <p className="text-[12.5px] text-slate-400 mt-1">
          No sales call unless you request one. We keep the result confidential
          to your property.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Full name" required>
          <input value={contact.name || ""}
                 onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))}
                 data-testid="assess-input-name"
                 className="w-full h-11 px-3 rounded-lg border border-white/15 bg-black/30 text-[13px] text-white" />
        </Field>
        <Field label="Work email" required>
          <input type="email" value={contact.email || ""}
                 onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
                 data-testid="assess-input-email"
                 className="w-full h-11 px-3 rounded-lg border border-white/15 bg-black/30 text-[13px] text-white" />
        </Field>
        <Field label="Phone">
          <input value={contact.phone || ""}
                 onChange={(e) => setContact((c) => ({ ...c, phone: e.target.value }))}
                 data-testid="assess-input-phone"
                 placeholder="(407) 555-0123"
                 className="w-full h-11 px-3 rounded-lg border border-white/15 bg-black/30 text-[13px] text-white" />
        </Field>
        <Field label="Your role at the property">
          <input value={contact.role || ""}
                 onChange={(e) => setContact((c) => ({ ...c, role: e.target.value }))}
                 data-testid="assess-input-role"
                 placeholder="Property Manager, Owner, HOA President…"
                 className="w-full h-11 px-3 rounded-lg border border-white/15 bg-black/30 text-[13px] text-white" />
        </Field>
        <Field label="Property / company name">
          <input value={contact.property_name || ""}
                 onChange={(e) => setContact((c) => ({ ...c, property_name: e.target.value }))}
                 data-testid="assess-input-property"
                 className="w-full h-11 px-3 rounded-lg border border-white/15 bg-black/30 text-[13px] text-white" />
        </Field>
        <Field label="City / state" hint="Florida properties get regulatory-adapted pricing.">
          <div className="flex gap-2">
            <input value={contact.city || ""}
                   onChange={(e) => setContact((c) => ({ ...c, city: e.target.value }))}
                   data-testid="assess-input-city"
                   placeholder="Orlando"
                   className="flex-1 h-11 px-3 rounded-lg border border-white/15 bg-black/30 text-[13px] text-white" />
            <input value={contact.state || ""}
                   onChange={(e) => setContact((c) => ({ ...c, state: e.target.value.toUpperCase().slice(0, 2) }))}
                   data-testid="assess-input-state"
                   placeholder="FL"
                   className="w-16 h-11 px-3 rounded-lg border border-white/15 bg-black/30 text-[13px] text-white" />
          </div>
        </Field>
        <Field label="Property type" required>
          <select value={contact.segment || ""}
                  onChange={(e) => setContact((c) => ({ ...c, segment: e.target.value }))}
                  data-testid="assess-input-segment"
                  className="w-full h-11 px-3 rounded-lg border border-white/15 bg-black/30 text-[13px] text-white">
            <option value="">Pick one…</option>
            {SEGMENTS.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </Field>
      </div>

      <div className="flex justify-end pt-3 border-t border-white/5">
        <button type="button" onClick={onNext} disabled={!canContinue}
          data-testid="assess-step1-next"
          className="h-11 px-5 rounded-lg text-[13px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-50"
          style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
          Continue <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}


// ── Step 2: 16 questions ──────────────────────────────────────────
function Step2({ questions, answers, setAnswers, onNext, onBack, submitting }) {
  const setA = (id, v) => setAnswers((a) => ({ ...a, [id]: v }));

  return (
    <div className="space-y-5" data-testid="assess-step2">
      <div>
        <div className="text-[10.5px] uppercase tracking-widest text-red-300 font-bold mb-1">
          Step 2 of 3
        </div>
        <h2 className="text-lg md:text-2xl font-extrabold text-white">
          Answer 16 short questions about your property
        </h2>
        <p className="text-[12.5px] text-slate-400 mt-1">
          Takes ~2 minutes. Every field is optional except the two integer ones.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {questions.map((q) => (
          <div key={q.id}
               data-testid={`assess-q-${q.id}`}
               className="rounded-xl border border-white/10 p-3 bg-black/20">
            <div className="text-[12px] text-slate-100 mb-2">{q.q}</div>
            {q.type === "int" && (
              <input type="number" min={q.min} max={q.max}
                     value={answers[q.id] ?? ""}
                     onChange={(e) => setA(q.id,
                       e.target.value === "" ? "" : Math.max(q.min ?? 0, Math.min(q.max ?? 99999, parseInt(e.target.value, 10) || 0)))}
                     data-testid={`assess-input-${q.id}`}
                     className="w-full h-10 px-3 rounded-lg border border-white/15 bg-black/30 text-[13px] text-white" />
            )}
            {q.type === "bool" && (
              <div className="flex gap-2">
                {[["yes", true], ["no", false]].map(([lbl, val]) => (
                  <button type="button"
                    key={lbl}
                    data-testid={`assess-input-${q.id}-${lbl}`}
                    onClick={() => setA(q.id, val)}
                    className={`flex-1 h-9 rounded-lg border text-[12px] font-bold uppercase transition-colors ${
                      answers[q.id] === val
                        ? "border-red-500 bg-red-500/15 text-red-100"
                        : "border-white/15 text-slate-300 hover:border-red-500/40"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            )}
            {q.type === "text" && (
              <input type="text" value={answers[q.id] ?? ""}
                     onChange={(e) => setA(q.id, e.target.value)}
                     data-testid={`assess-input-${q.id}`}
                     placeholder="Optional"
                     className="w-full h-10 px-3 rounded-lg border border-white/15 bg-black/30 text-[13px] text-white" />
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-white/5">
        <button type="button" onClick={onBack}
          data-testid="assess-step2-back"
          className="h-11 px-4 rounded-lg text-[12px] font-bold text-slate-100 border border-white/20 inline-flex items-center gap-1.5">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button type="button" onClick={onNext} disabled={submitting}
          data-testid="assess-step2-submit"
          className="h-11 px-5 rounded-lg text-[13px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-50"
          style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {submitting ? "Analyzing…" : "See my score"}
        </button>
      </div>
    </div>
  );
}


// ── Step 3: results ───────────────────────────────────────────────
function Step3({ result }) {
  if (!result) return null;
  const r = result;
  const tierColor = r.tier_color || "#EAB308";
  const tierBg = `${tierColor}22`;
  const pkg = r.recommended_package;

  return (
    <div className="space-y-5" data-testid="assess-step3">
      <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-3 text-[12px] text-emerald-100 flex items-start gap-2"
           data-testid="assess-confirmation">
        <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-300" />
        <div>
          Your assessment was saved. A Smith Protective advisor will reach out
          within one business day to confirm details and schedule a private
          site walkthrough.
        </div>
      </div>

      {/* Two-score summary */}
      <div className="grid md:grid-cols-2 gap-3">
        <div className="rounded-2xl border border-white/10 p-4"
             data-testid="assess-vehicle-score">
          <div className="text-[10.5px] uppercase tracking-widest text-slate-400 font-bold mb-1">
            Vehicle Security Opportunity Score
          </div>
          <div className="rounded-lg p-4" style={{ background: tierBg }}>
            <div className="flex items-baseline gap-3">
              <span className="text-[48px] font-extrabold tabular-nums leading-none"
                    style={{ color: tierColor }}>
                {r.vehicle_security_score}
              </span>
              <span className="text-[14px] font-bold" style={{ color: tierColor }}>
                {r.tier_label} tier
              </span>
            </div>
          </div>
          <ul className="mt-3 space-y-1 text-[11.5px] text-slate-200">
            {(r.score_reasons || []).slice(0, 6).map((rea, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <CheckCircle2 className="w-3 h-3 text-emerald-400 mt-1 shrink-0" />
                <span>{rea}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border border-white/10 p-4"
             data-testid="assess-risk-score">
          <div className="text-[10.5px] uppercase tracking-widest text-slate-400 font-bold mb-1">
            Property Risk Score
          </div>
          <div className="rounded-lg p-4 bg-red-500/10">
            <div className="flex items-baseline gap-3">
              <span className="text-[48px] font-extrabold text-red-400 tabular-nums leading-none">
                {r.risk_score}
              </span>
              <span className="text-[14px] font-bold text-red-300">
                / 100 risk exposure
              </span>
            </div>
          </div>
          <div className="mt-3 space-y-2 text-[12px] text-slate-200">
            <div className="flex items-center gap-2">
              <Radio className="w-4 h-4 text-red-300" />
              <span>{r.patrol_recommendation}</span>
            </div>
            <div className="flex items-center gap-2">
              <UserCheck className="w-4 h-4 text-red-300" />
              <span>{r.officer_recommendation}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recommended package */}
      {pkg && (
        <div className="rounded-2xl border border-red-500/40 p-5"
             style={{ background: "linear-gradient(120deg, rgba(220,38,38,0.10), rgba(127,29,29,0.06))" }}
             data-testid="assess-package">
          <div className="flex items-center gap-1.5 mb-2">
            <Package className="w-4 h-4 text-red-300" />
            <div className="text-[10.5px] uppercase tracking-widest text-red-200 font-bold">
              Recommended Package
            </div>
          </div>
          <div className="text-[18px] font-extrabold text-white">
            Package {pkg.package} — {pkg.name}
          </div>
          <div className="text-[12px] text-slate-300 mt-1">{pkg.reason}</div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {pkg.includes.map((it) => (
              <span key={it}
                data-testid={`assess-package-include-${it.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-[11px] px-2 py-0.5 rounded border border-red-500/40 bg-red-500/10 text-red-100">
                {it}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Deployment specifics */}
      <div className="grid md:grid-cols-3 gap-3">
        <div className="rounded-xl border border-white/10 p-4"
             data-testid="assess-cameras">
          <div className="text-[10.5px] uppercase tracking-widest text-slate-400 font-bold mb-1">
            Recommended ALPR cameras
          </div>
          <div className="text-[28px] font-extrabold text-white tabular-nums">
            {r.recommended_alpr_cameras}
          </div>
          <div className="text-[11.5px] text-slate-400 leading-tight">
            {r.suggested_cctv_coverage}
          </div>
        </div>
        <div className="rounded-xl border border-white/10 p-4">
          <div className="text-[10.5px] uppercase tracking-widest text-slate-400 font-bold mb-1">
            Estimated technology contract
          </div>
          <div className="text-[18px] font-bold text-white tabular-nums"
               data-testid="assess-estimated">
            {r.estimated_opportunity?.headline_range || "—"}
          </div>
          <div className="text-[10.5px] text-slate-500 mt-0.5">
            + recurring services · final scope after site walk.
          </div>
        </div>
        <div className="rounded-xl border border-white/10 p-4">
          <div className="text-[10.5px] uppercase tracking-widest text-slate-400 font-bold mb-1">
            Suggested next meeting
          </div>
          <div className="text-[13px] font-bold text-white leading-snug"
               data-testid="assess-next-meeting">
            {r.suggested_next_meeting}
          </div>
        </div>
      </div>

      {/* Cross-sell */}
      {r.recommended_cross_sell?.length > 0 && (
        <div className="rounded-2xl border border-white/10 p-4"
             data-testid="assess-cross-sell">
          <div className="text-[10.5px] uppercase tracking-widest text-slate-400 font-bold mb-2">
            Additional recommendations
          </div>
          <div className="space-y-2">
            {r.recommended_cross_sell.map((rec, i) => (
              <div key={i}
                data-testid={`assess-cross-sell-${i}`}
                className="rounded-lg border border-white/10 bg-black/20 p-2.5">
                <div className="text-[12.5px] font-bold text-white">{rec.headline}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">{rec.reason}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Regulatory */}
      <div className="rounded-lg border border-amber-500/25 bg-amber-500/5 p-3 text-[11px] text-amber-200 flex items-start gap-2">
        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <div>
          Every recommendation must be reviewed against your local Florida
          jurisdiction — HOA authority, signage, retention limits and
          law-enforcement sharing rules vary. Smith SecureVehicle™ provides
          Private Vehicle Intelligence with customer-controlled policies.
        </div>
      </div>

      <div className="flex justify-between pt-3 border-t border-white/5">
        <Link to="/private-vehicle-security"
              data-testid="assess-back-landing"
              className="h-11 px-4 rounded-lg text-[12px] font-bold text-slate-100 border border-white/20 inline-flex items-center gap-1.5">
          <Home className="w-4 h-4" /> Back to overview
        </Link>
        <a href="tel:+18005550100"
          data-testid="assess-call-cta"
          className="h-11 px-5 rounded-lg text-[13px] font-bold text-white inline-flex items-center gap-1.5"
          style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
          <Radio className="w-4 h-4" /> Talk to a Smith advisor now
        </a>
      </div>
    </div>
  );
}


// ── Root component ─────────────────────────────────────────────────
export default function SecurityAssessment() {
  useSeo();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [questions, setQuestions] = useState([]);
  const [contact, setContact] = useState({ state: "FL" });
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/vehicle-security/assessment/questions`);
        setQuestions(r.data?.questions || []);
      } catch {
        toast.error("Failed to load questions — try refreshing.");
      }
    })();
  }, []);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/vehicle-security/assessment/submit`,
        { contact, answers },
        { timeout: 30000 });
      setResult(r.data?.result || null);
      setStep(3);
      toast.success("Assessment received — see your recommendations below.");
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  return (
    <main className="min-h-screen"
          data-testid="assessment-page"
          style={{ background: "#0B1120", color: "#E2E8F0",
                    fontFamily: "'Inter',system-ui,sans-serif" }}>

      <header className="border-b border-white/5">
        <div className="max-w-[1000px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/private-vehicle-security" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                 style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
              <Car className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-[14px] font-extrabold text-white">
                Smith SecureVehicle™
              </div>
              <div className="text-[10px] text-slate-400 -mt-0.5">
                Free 2-minute assessment
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-1.5 text-[10.5px] text-slate-400">
            <span data-testid="assess-progress"
                  className="uppercase tracking-widest font-bold">
              Step {step === 3 ? "3" : step} of 3
            </span>
          </div>
        </div>
      </header>

      <section className="max-w-[1000px] mx-auto px-6 py-10">
        {/* Progress bar */}
        <div className="rounded-full h-1.5 bg-white/10 mb-8 overflow-hidden">
          <div className="h-full transition-all"
               data-testid="assess-progress-bar"
               style={{
                 width: `${(step / 3) * 100}%`,
                 background: "linear-gradient(90deg,#DC2626,#7F1D1D)",
               }} />
        </div>

        <div className="rounded-2xl border border-white/10 p-5 md:p-7 bg-black/20">
          {step === 1 && (
            <Step1 contact={contact} setContact={setContact}
                    onNext={() => setStep(2)} />
          )}
          {step === 2 && (
            <Step2 questions={questions} answers={answers} setAnswers={setAnswers}
                    onBack={() => setStep(1)}
                    onNext={submit}
                    submitting={submitting} />
          )}
          {step === 3 && <Step3 result={result} />}
        </div>

        <div className="text-center text-[10.5px] text-slate-500 mt-6">
          Your data stays private. Read our{" "}
          <Link to="/privacy" className="hover:text-slate-300 underline">
            privacy policy
          </Link>.
        </div>
      </section>
    </main>
  );
}
